/**
 * Sidebar.js — desktop side navigation.
 *
 * RENDER ONLY. All visibility logic lives in /config/accessControl.js.
 * To change which items a role sees, edit that config — never this file.
 */
import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, LogOut } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import usePanelCounts from '../../hooks/usePanelCounts';
import useAccessOverrides from '../../hooks/useAccessOverrides';
import { getNavForRole } from '../../config/accessControl';

function NavBadge({ count, testId }) {
  if (!count || count <= 0) return null;
  return (
    <span
      className="ml-auto h-5 min-w-[20px] px-1.5 bg-[#DC2626] text-white text-[10px] font-bold flex items-center justify-center tabular-nums"
      data-testid={testId}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

// Map a route path to the correct count from /api/notifications/panel-counts.
function badgeForPath(path, counts) {
  if (!path || !counts) return 0;
  if (path.startsWith('/task-management') || path === '/tasks' || path.startsWith('/tasks/')) return counts.tasks;
  if (path === '/calendar' || path === '/book-meeting' || path.startsWith('/calendar/')) return counts.meetings;
  if (path.startsWith('/inspections')) return counts.inspections;
  if (path === '/chat' || path === '/groups' || path.startsWith('/chat/')) return counts.chat;
  if (path === '/notifications') return counts.notifications;
  if (path.startsWith('/stock-transfers')) return counts.stock_transfers;
  if (path === '/approvals') return counts.approvals;
  return 0;
}

// Map a route path to the category understood by
// POST /api/notifications/mark-category-read. Keep in sync with
// badgeForPath above so clicking a sidebar item clears its red badge.
function categoryForPath(path) {
  if (!path) return null;
  if (path.startsWith('/task-management') || path === '/tasks' || path.startsWith('/tasks/')) return 'tasks';
  if (path === '/calendar' || path === '/book-meeting' || path.startsWith('/calendar/')) return 'meetings';
  if (path.startsWith('/inspections')) return 'inspections';
  if (path === '/chat' || path === '/groups' || path.startsWith('/chat/')) return 'messages';
  if (path === '/notifications') return 'all';
  if (path.startsWith('/stock-transfers')) return 'stock_transfers';
  if (path === '/approvals') return 'approvals';
  return null;
}

async function markCategoryReadIfBadged(path, count) {
  if (!count || count <= 0) return;
  const category = categoryForPath(path);
  if (!category) return;
  try {
    const { apiRequest } = await import('../../lib/utils');
    await apiRequest(`/notifications/mark-category-read?category=${category}`, { method: 'POST' });
    try { window.dispatchEvent(new CustomEvent('notifications:read')); } catch (_) {}
  } catch (err) {
    console.warn('Failed to mark category read on nav click:', err);
  }
}

function NavItem({ item, counts, testIdPrefix }) {
  const Icon = item.icon;
  const testId = `${testIdPrefix}-${item.key}`;
  const count = badgeForPath(item.path, counts);
  return (
    <NavLink
      to={item.path}
      end={item.path === '/'}
      onClick={() => markCategoryReadIfBadged(item.path, count)}
      className={({ isActive }) => `nav-item ${isActive ? 'nav-item-active' : ''}`}
      data-testid={testId}
    >
      <Icon size={20} />
      <span className="flex-1">{item.label}</span>
      <NavBadge count={count} testId={`${testId}-badge`} />
    </NavLink>
  );
}

function Dropdown({ dropdown, counts, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const Icon = dropdown.icon;
  const total = dropdown.items.reduce(
    (sum, it) => sum + (badgeForPath(it.path, counts) || 0), 0
  );
  if (dropdown.items.length === 0) return null;
  return (
    <div className="nav-section">
      <button
        onClick={() => setOpen(!open)}
        className="nav-section-header"
        data-testid={`sidebar-dropdown-${dropdown.key}`}
      >
        <div className="flex items-center gap-2">
          <Icon size={18} className="text-slate-500" />
          <span className="font-bold text-sm text-slate-700">{dropdown.label}</span>
        </div>
        <div className="flex items-center gap-2">
          <NavBadge count={total} testId={`sidebar-dropdown-${dropdown.key}-badge`} />
          {open ? <ChevronDown size={16} className="text-slate-400" />
                : <ChevronRight size={16} className="text-slate-400" />}
        </div>
      </button>
      {open && (
        <div className="nav-section-items">
          {dropdown.items.map(item => (
            <NavItem
              key={item.key}
              item={item}
              counts={counts}
              testIdPrefix={`sidebar-${dropdown.key}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SidebarFooter({ user, onLogout }) {
  return (
    <div className="border-t border-slate-200 bg-slate-50 mt-auto">
      <NavLink
        to="/profile"
        className={({ isActive }) => `flex items-center gap-3 px-3 py-3 transition-colors border-l-2 ${isActive ? 'border-[#DC2626] bg-white' : 'border-transparent hover:bg-white'}`}
        data-testid="sidebar-profile-footer"
      >
        <div className="w-8 h-8 bg-slate-900 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
          {(user?.name || '?').slice(0, 1).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-900 truncate">{user?.name}</p>
          <p className="text-[10px] uppercase tracking-wider text-slate-500 truncate font-semibold">{(user?.role || '').replace(/_/g, ' ')}</p>
        </div>
      </NavLink>
      <button
        onClick={onLogout}
        className="w-full flex items-center gap-2 px-3 py-2 text-xs text-slate-600 hover:bg-red-50 hover:text-red-700 uppercase tracking-wider font-bold border-t border-slate-200"
        data-testid="sidebar-logout"
      >
        <LogOut size={14} strokeWidth={2.5} />
        <span>Sign Out</span>
      </button>
    </div>
  );
}

export default function Sidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const counts = usePanelCounts();
  const overrides = useAccessOverrides();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const role = user?.role;
  if (!role) return null;
  const { common, dropdowns, issueReporting } = getNavForRole(role, overrides, {
    isFranchiseStore: !!user?.is_franchise_store,
  });

  return (
    <aside className="hidden lg:flex flex-col w-64 bg-white border-r border-slate-300 h-screen fixed left-0 top-0 z-30">
      <div className="px-4 py-4 border-b border-slate-300 bg-white">
        <NavLink to="/dashboard" data-testid="sidebar-logo-link" className="flex items-center gap-2">
          <img src="/pie-logo.png" alt="PIE" className="h-10" />
        </NavLink>
      </div>

      <nav className="flex-1 overflow-y-auto py-2 space-y-0">
        {common.length > 0 && (
          <div className="px-3 py-1.5 mb-1">
            <span className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-bold">Main</span>
          </div>
        )}
        {common.map(item => (
          <NavItem key={item.key} item={item} counts={counts} testIdPrefix="sidebar-common" />
        ))}

        {dropdowns.length > 0 && (
          <div className="pt-3 mt-2 border-t border-slate-200">
            <div className="px-3 py-1.5 mb-1">
              <span className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-bold">Modules</span>
            </div>
            {dropdowns.map((d) => (
              <Dropdown key={d.key} dropdown={d} counts={counts} defaultOpen={false} />
            ))}
          </div>
        )}

        {issueReporting && (
          <div className="pt-3 mt-2 border-t border-slate-200">
            <NavItem item={issueReporting} counts={counts} testIdPrefix="sidebar-common" />
          </div>
        )}
      </nav>

      <SidebarFooter user={user} onLogout={handleLogout} />
    </aside>
  );
}
